import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'intervantis_theme_mode';
const ThemeContext = createContext({ mode: 'system', setMode: () => {} });

const mql = () => window.matchMedia('(prefers-color-scheme: dark)');
const resolve = (mode) => (mode === 'system' ? (mql().matches ? 'dark' : 'light') : mode);
const apply = (mode) => {
  const root = document.documentElement;
  const dark = resolve(mode) === 'dark';
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => localStorage.getItem(STORAGE_KEY) || 'system');

  useEffect(() => {
    apply(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // Keep "system" mode in sync with the OS — via the media-query event AND on
  // tab focus/visibility (covers browsers that miss the event while backgrounded).
  useEffect(() => {
    const m = mql();
    const syncIfSystem = () => {
      if ((localStorage.getItem(STORAGE_KEY) || 'system') === 'system') apply('system');
    };
    if (m.addEventListener) m.addEventListener('change', syncIfSystem);
    else if (m.addListener) m.addListener(syncIfSystem); // older Safari
    document.addEventListener('visibilitychange', syncIfSystem);
    window.addEventListener('focus', syncIfSystem);
    window.addEventListener('pageshow', syncIfSystem);
    return () => {
      if (m.removeEventListener) m.removeEventListener('change', syncIfSystem);
      else if (m.removeListener) m.removeListener(syncIfSystem);
      document.removeEventListener('visibilitychange', syncIfSystem);
      window.removeEventListener('focus', syncIfSystem);
      window.removeEventListener('pageshow', syncIfSystem);
    };
  }, []);

  const setMode = useCallback((v) => setModeState(v), []);

  return (
    <ThemeContext.Provider value={{ mode, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
